/* eslint-disable camelcase */

export interface Ga4BootstrapOptions {
  /** GA4 measurement ID. Shared by every club unless a club overrides it, see ConfigurationService. */
  measurementId: string;
  /** Club key (ConfigurationService.environment), sent as both a user property and an event parameter. */
  client: string;
  /** Surfaces events in GA4 DebugView without counting them in reports. */
  debug: boolean;
}

/**
 * Boots gtag.js for a single GA4 property shared by every club.
 *
 * Every club reports into one measurement ID by default; they are told apart by the
 * `client` dimension, registered in GA4 twice (Admin > Custom definitions):
 *   - event-scoped dimension over the `client` event parameter, for report filters and
 *     comparisons, the config parameters below ride along on every event.
 *   - user-scoped dimension over the `client` user property, for audiences and
 *     user-level metrics.
 *
 * A club may instead override `ga4MeasurementId` in its configuration to report into its
 * own property (see ConfigurationService), in that case this still runs once, with that
 * club's own ID, and the `client` dimension is simply unused on that property.
 *
 * `send_page_view` is off because AnalyticsService owns page_view: it fires one per
 * Angular NavigationEnd, and the automatic hit would double-count the first route.
 */
export function initGa4(options: Ga4BootstrapOptions): void {
  const { measurementId, client, debug } = options;

  // Set up the dataLayer stub before the async script loads so events queued during that
  // window are not lost — gtag.js drains the queue once it initialises.
  const globalRecord = globalThis as unknown as Record<string, unknown>;
  globalRecord['dataLayer'] ??= [];
  // Must use a regular function (not arrow) so `arguments` is the native IArguments object.
  // gtag.js checks !(item instanceof Array) when draining the dataLayer queue — a plain Array
  // from rest-params fails that check and the event is silently dropped.
  globalRecord['gtag'] = function gtag(): void {
    // eslint-disable-next-line prefer-rest-params
    (globalRecord['dataLayer'] as unknown[]).push(arguments);
  };

  const gtagFunction = globalRecord['gtag'] as (
    ...arguments_: unknown[]
  ) => void;
  gtagFunction('js', new Date());
  // User-scoped: sticks to the visitor for the whole session, so user metrics and
  // audiences resolve to one club even on hits that carry no event parameters.
  gtagFunction('set', 'user_properties', { client });
  // Event-scoped: parameters passed to `config` are appended to every subsequent event
  // on this measurement ID, so AnalyticsService needs no per-call client plumbing.
  gtagFunction('config', measurementId, {
    client,
    send_page_view: false,
    ...(debug && { debug_mode: true }),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.append(script);
}
